<template>
  <div
    :class="['v-product-thumbnail-image-container', {'image-cursor':isLargeCard }]"
  >
    <div
      v-if="imageBadge?.enableBadge"
      class="badge-container"
    >
      <div
        :class="[
          { 'badge-icon': imageBadge?.badgeText === 'Gifted' },
          iconName,
        ]"
      />
      <div
        :class="['badge-text', { 'gifted': imageBadge?.badgeText === 'Gifted' }]"
        v-html="sanitizedBadgeText"
      />
    </div>

    <div
      v-if="jumboSwatch?.hoveringState"
      :class="['jumboSwatch', { 'showJumbo': jumboSwatch?.hoveringState }]"
    >
      <span :style="[`background: url(${ jumboSwatch?.swatchUrl}); background-repeat: no-repeat; background-position-x: ${jumboSwatch?.offset}%;`]" />
    </div>

    <div :class="[imageBadge?.enableBadge? 'badge-mask': '']">
      <Carousel
        v-if="getImagePaths?.length > 1 && isLargeCard && !slideshowProps?.useSlideshow"
        :list="getImagePaths"
        :number-of-items="{ large: 1.1, medium: 1.1, small: 1.1 }"
        :show-indicator="true"
        :threshold-limit="0.9"
      >
        <template #item="slotProps">
          <Picture
            :image-url="slotProps?.data?.url"
            :image-intrinsic-width="imageWidth"
            :aspect-ratio="imageAspectRatio"
            :image-sizes-by-breakpoints="imageSizesByBreakpoints"
            :set-scene7-hei="true"
            :skeleton="skeleton"
            :skeleton-options="skeletonOptions"
            :retina-adjustment="retinaAdjustment"
            :retina-quality="retinaQuality"
            :draggable="draggable"
            :fetch-priority="fetchPriority"
            :lazy-load="lazyLoad"
          />
        </template>
      </Carousel>
      <Slideshow
        v-else-if="(slideshowProps?.renderSlideshowOnMount ? mounted : true) && getImagePaths?.length >= 2 && showSlideshow"
        :autoplay-mode="sliderPlayMode"
        :back-to-first="slideshowProps?.backToFirst"
        :autoplay-interval="slideshowProps?.autoplayInterval"
        :list="getImagePaths"
        :show-indicator="slideshowProps?.showIndicator"
        :show-indicator-on-slideshow="slideshowProps?.showIndicatorOnSlideshow"
        :show-skeleton="slideshowProps?.showSkeleton"
        :skeleton-options="{
          paddingBottom: `${(100 / imageAspectRatio)}%`
        }"
        @slideshow-change-click="chevronDirection"
      >
        <template #item="slotProps">
          <Picture
            :alt="alt"
            :image-url="slotProps?.data?.url "
            :image-intrinsic-width="imageWidth"
            :aspect-ratio="imageAspectRatio"
            :image-sizes-by-breakpoints="imageSizesByBreakpoints"
            :set-scene7-hei="true"
            :skeleton="skeleton"
            :skeleton-options="skeletonOptions"
            :retina-adjustment="retinaAdjustment"
            :retina-quality="retinaQuality"
            :draggable="draggable"
            :fetch-priority="highPriorityImage(slotProps, 'fetch')"
            :lazy-load="highPriorityImage(slotProps, 'lazy')"
            :native-lazy="setNativeLazy(slotProps.index)"
          />
        </template>
      </Slideshow>
      <Picture
        v-else
        :alt="alt"
        :image-url="getImagePaths[0]?.url"
        :image-intrinsic-width="imageWidth"
        :aspect-ratio="imageAspectRatio"
        :image-sizes-by-breakpoints="imageSizesByBreakpoints"
        :set-scene7-hei="true"
        :skeleton="skeleton"
        :skeleton-options="skeletonOptions"
        :retina-adjustment="retinaAdjustment"
        :retina-quality="retinaQuality"
        :draggable="draggable"
        :fetch-priority="fetchPriority"
        :lazy-load="lazyLoad"
        :native-lazy="setNativeLazy(0)"
      />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import Picture from '@atomic-ui/picture';
import Slideshow from '@atomic-ui/slideshow';
import Carousel from '@atomic-ui/carousel';
import { sanitize } from '@vue-util/sanitize';
import constructUrl from '../../utils/constructURL';

export default {
  name: 'ProductThumbnailImage',
  components: {
    Picture,
    Slideshow,
    Carousel,
  },
  props: {
    imagery: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    traits: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    selectedColor: {
      type: Number,
      required: false,
      default: undefined,
    },
    urlTemplate: {
      type: String,
      required: false,
      default: '',
    },
    alignment: {
      type: String,
      required: false,
      default: 'vertical',
    },
    // TODO:  remove prop after grouping the props
    disableSlideshow: {
      type: Boolean,
      required: false,
      default: false,
    },
    alt: {
      type: String,
      required: false,
      default: '',
    },
    imageSizesByBreakpoints: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    imageAspectRatio: {
      type: Number,
      required: false,
      default: 0.8,
    },
    imageWidth: {
      type: Number,
      required: false,
    },
    isLargeCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    imageBadge: {
      type: Object,
      required: false,
      default: () => ({
        enableBadge: false,
        badgeText: '',
      }),
    },
    selectedSizeImages: {
      type: Array,
      required: false,
      default: undefined,
    },
    inStoreOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    slideshowProps: {
      type: Object,
      required: false,
      default: () => ({ }),
    },
    skeleton: {
      type: Boolean,
      required: false,
    },
    skeletonOptions: {
      type: Object,
      default: () => ({}),
    },
    retinaAdjustment: {
      type: Boolean,
      required: false,
      default: false,
    },
    retinaQuality: {
      type: String,
      required: false,
      default: '55,0',
    },
    draggable: {
      type: String,
      required: false,
      default: 'auto',
    },
    fetchPriority: {
      type: Boolean,
      required: false,
      default: false,
    },
    isHovering: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    colorSwatch: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: true,
    },
    firstImagePriority: {
      type: Boolean,
      required: false,
      default: false,
    },
    firstPictureNativeLazy: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['chevronDirection'],
  setup(props, { emit }) {
    const mounted = ref();
    const defaultImagePaths = computed(() => {
      let imagePaths = [];
      let productUrlTemplate = '';
      // if colorMap has more than 1 item
      if (props.traits.colors) {
        if (props.traits.colors?.colorMap?.length || props.traits.colors?.selectedColor) {
        // find selected color (from xapi or swatch selection)
          let selectedColorTrait = props.traits.colors?.colorMap?.find((color) => color.id === props.selectedColor)
          || props.traits.colors?.selectedColor;
          // using first item if can't find
          if (selectedColorTrait === undefined) {
            [selectedColorTrait] = props.traits.colors.colorMap;
          }
          // from object, create array of image urls
          if (selectedColorTrait?.imagery?.additionalImageSource) {
            imagePaths = selectedColorTrait.imagery.additionalImageSource.map((image) => image.filePath);
          }
          // set primaryImage to be first image, otherwise use first item
          if (selectedColorTrait?.imagery?.primaryImage?.filePath) {
            imagePaths = imagePaths.filter((item) => item !== selectedColorTrait.imagery.primaryImage.filePath);
            imagePaths.unshift(selectedColorTrait.imagery.primaryImage.filePath);
          }
        }
      }
      if (!imagePaths.length && props.imagery?.primaryImage?.filePath) {
        imagePaths.unshift(props.imagery.primaryImage.filePath);
      } else if (!imagePaths.length && props.imagery?.images?.length) {
        imagePaths.unshift(props.imagery.images[0].filePath);
      }
      productUrlTemplate = props.imagery?.urlTemplate
        ? props.imagery?.urlTemplate
        : props.urlTemplate;
      return imagePaths.map((filePath) => ({
        url: constructUrl(productUrlTemplate, filePath, props.imageWidth),
      }));
    });

    const jumboSwatch = computed(() => {
      if (props.traits?.colors?.colorMap?.length) {
        const {
          colors, swatchUrl, selectedColor, initial,
        } = props.colorSwatch;
        const selectedColorTrait = props.traits.colors.colorMap.find((color) => color.id === props.isHovering.color) || selectedColor;
        if (selectedColorTrait && colors) {
          const selectedColorOffset = colors.find((color) => color.id === selectedColorTrait.id);
          const { offset } = selectedColorOffset;
          const showingJumbo = props.traits.colors.colorMap.length > 1 && selectedColorOffset.showJumboSwatch;
          let hoveringState;
          // hide for the first colorSwatch update
          if (!props.isHovering.state && !initial) {
            hoveringState = showingJumbo;
          } else {
            hoveringState = showingJumbo && props.isHovering.state;
          }
          return { offset, swatchUrl, hoveringState };
        }
      }
      return {};
    });

    const iconName = computed(() => {
      switch (props.imageBadge.badgeText) {
      case 'Gifted':
        return 'heart-box';
      default:
        return 'def';
      }
    });
    const getImagePaths = computed(() => {
      if (props.selectedSizeImages?.length > 0) {
        return props.selectedSizeImages.map((img) => ({
          url: props.urlTemplate + img.filePath,
        }));
      }
      return defaultImagePaths.value;
    });
    const sanitizedBadgeText = computed(() => {
      const options = {
        whiteList: {
          a: ['href', 'class', 'onclick'],
        },
      };
      return sanitize(props.imageBadge.badgeText, options);
    });

    // Create a cache using a Map object to store memoized results
    const cache = new Map();

    const highPriorityImage = computed(() => (slotProps, source) => {
      // Create a unique cache key based on url from slotProps and source
      const cacheKey = `${slotProps.data?.url}-${source}`;
      // Check if the result is already cached
      if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
      }
      const { firstImagePriority, fetchPriority, lazyLoad } = props;
      // Determine the result based on firstImagePriority, fetchPriority, and source
      let result;
      if (firstImagePriority && fetchPriority) {
        if (source === 'fetch') {
          result = slotProps.index === 0;
        } else if (source === 'lazy') {
          result = slotProps.index !== 0;
        }
      } else {
        result = source === 'fetch' ? fetchPriority : lazyLoad;
      }
      cache.set(cacheKey, result);
      return result;
    });

    const chevronDirection = (event) => {
      emit('chevronDirection', event);
    };

    const setNativeLazy = (index) => !props.fetchPriority && props.firstPictureNativeLazy && index === 0;

    onMounted(() => {
      mounted.value = true;
    });

    return {
      mounted,
      iconName,
      getImagePaths,
      sanitizedBadgeText,
      jumboSwatch,
      highPriorityImage,
      chevronDirection,
      setNativeLazy,
    };
  },
  computed: {
    showSlideshow() {
      if (this.alignment === 'vertical') {
        return !(this.disableSlideshow || this.slideshowProps.disableSlideshow);
      }
      return false;
    },
    sliderPlayMode() {
      if (this.getImagePaths?.length < 2) {
        return 'manual';
      }

      return this.slideshowProps.autoplayMode;
    },
  },
};
</script>
<style lang="scss" scoped>
.v-product-thumbnail-image-container {
  position: relative;
  container-type: inline-size;

  .badge-container {
    position: absolute;
    background-color: $white;
    border-radius: $global-border-radius;
    padding:rem-calc(0 16);
    z-index: 1;
    left: 50%;
    top: 50%;
    max-width: 100%;
    white-space: nowrap;
    transform: translate(-50%, -50%);
    opacity: 0.9;
    .badge-icon,
    .badge-text {
      display: inline-block;
    }
    .badge-text {
      text-align: center;
      padding: rem-calc(12 0);
    }
    .badge-icon {
      margin-right: $global-margin;
      padding: rem-calc(10 0);
    }
    .gifted {
      vertical-align: super;
    }
  }

  .badge-mask:after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(255, 255, 255, 0.3);
  }

  @container (width <= 180px) {
    .badge-container {
      max-width: 80%;
      min-width: rem-calc(113);

      .badge-text {
        white-space: normal;
      }
    }
  }
}

.image-cursor{
  cursor: default;
}

.jumboSwatch {
  visibility: hidden;
  background: none repeat scroll 0 0 #fff;
  box-sizing: content-box;
  position: absolute;
  z-index: 1;

  &.showJumbo {
    visibility: visible;
  }
}

@include breakpoint(medium down) {
.jumboSwatch {
  &.showJumbo {
    visibility: hidden;
  }
 }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "@csgIcons/_registry-heart-box.scss";
.jumboSwatch {
  right: 0;
  bottom: 0;
  margin-bottom: rem-calc(16.5px);
  margin-right: rem-calc(8);
  border: rem-calc(1px) solid #000;
  span {
    width: rem-calc(64px);
    height: rem-calc(64px);
    display: block;
    margin: rem-calc(3px);
  }
}
.badge-icon.heart-box {
  @extend .registry-heart-box;
}

</style>
<style lang="scss" brand="bcom" scoped>
@import "@csgIcons/_registry-box.scss";
.badge-icon.heart-box {
  @extend .registry-box;
}
.jumboSwatch {
    border: rem-calc(1px) solid #cacaca;
    bottom: rem-calc(5px);
    box-shadow: rem-calc(5px) rem-calc(5px) 0 rgba(0,0,0,.2);
    height: 19%;
    padding: rem-calc(8px);
    right: rem-calc(5px);
    width: 24%;
    max-width: rem-calc(82px);
    max-height: rem-calc(82px);

    span {
      width: 100%;
      height: 100%;
      display: block;
    }
}
</style>
